import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComparisonModal = ({ 
  photos,
  selectedPhotoId,
  onClose
}) => {
  const [compareWithId, setCompareWithId] = useState(null);

  const selectedPhoto = photos?.find(p => p?.id === selectedPhotoId);
  const comparePhoto = photos?.find(p => p?.id === compareWithId);

  const getScoreColor = (score) => {
    if (score >= 8) return 'var(--color-success)';
    if (score >= 6) return 'var(--color-primary)';
    if (score >= 4) return 'var(--color-warning)';
    return 'var(--color-error)';
  };

  const getScoreDifference = (score1, score2) => {
    const diff = score1 - score2;
    return {
      value: Math.abs(diff)?.toFixed(1),
      isPositive: diff > 0,
      color: diff > 0 ? 'var(--color-success)' : diff < 0 ? 'var(--color-error)' : 'var(--color-muted-foreground)'
    };
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center">
      <div className="bg-card rounded-t-2xl sm:rounded-xl border border-border w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header - Touch Optimized */}
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
          <h2 className="text-lg font-semibold text-foreground">Compare Photos</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            className="h-10 w-10"
          />
        </div>

        <div className="p-4">
          {!compareWithId ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Select a photo to compare with Rank #{selectedPhoto?.rank}
              </p>
              {/* Horizontal Scrollable Photo Selection */}
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory">
                {photos?.filter(p => p?.id !== selectedPhotoId)?.map((photo) => (
                  <button
                    key={photo?.id}
                    onClick={() => setCompareWithId(photo?.id)}
                    className="group relative flex-shrink-0 w-32 h-40 rounded-lg overflow-hidden border-2 border-border active:border-primary transition-all snap-start"
                  >
                    <Image
                      src={photo?.image}
                      alt={photo?.imageAlt}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end p-2">
                      <div className="text-white">
                        <div className="text-xs mb-0.5">Rank #{photo?.rank}</div>
                        <div className="text-base font-bold">{photo?.overallScore?.toFixed(1)}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-foreground">Comparison</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCompareWithId(null)}
                  iconName="ArrowLeft"
                  iconPosition="left"
                  className="text-xs h-9"
                >
                  Change
                </Button>
              </div>

              {/* Single Column Comparison */}
              <div className="space-y-4">
                {/* Photo 1 */}
                <div className="space-y-3">
                  <div className="relative aspect-[4/5] rounded-lg overflow-hidden bg-muted">
                    <Image
                      src={selectedPhoto?.image}
                      alt={selectedPhoto?.imageAlt}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 left-2 bg-primary text-primary-foreground px-3 py-1 rounded-lg font-bold text-sm">
                      Rank #{selectedPhoto?.rank}
                    </div>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3">
                    <div className="text-center mb-3">
                      <div 
                        className="text-3xl font-bold mb-1"
                        style={{ color: getScoreColor(selectedPhoto?.overallScore) }}
                      >
                        {selectedPhoto?.overallScore?.toFixed(1)}
                      </div>
                      <div className="text-xs text-muted-foreground">Overall Score</div>
                    </div>
                    <div className="space-y-1.5">
                      {selectedPhoto?.criteria?.map((criterion, index) => (
                        <div key={index} className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">{criterion?.name}</span>
                          <span 
                            className="font-semibold"
                            style={{ color: getScoreColor(criterion?.score) }}
                          >
                            {criterion?.score?.toFixed(1)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground font-medium">VS</span>
                  <div className="flex-1 h-px bg-border" />
                </div>

                {/* Photo 2 */}
                <div className="space-y-3">
                  <div className="relative aspect-[4/5] rounded-lg overflow-hidden bg-muted">
                    <Image
                      src={comparePhoto?.image}
                      alt={comparePhoto?.imageAlt}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 left-2 bg-secondary text-secondary-foreground px-3 py-1 rounded-lg font-bold text-sm">
                      Rank #{comparePhoto?.rank}
                    </div>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3">
                    <div className="text-center mb-3">
                      <div 
                        className="text-3xl font-bold mb-1"
                        style={{ color: getScoreColor(comparePhoto?.overallScore) }}
                      >
                        {comparePhoto?.overallScore?.toFixed(1)}
                      </div>
                      <div className="text-xs text-muted-foreground">Overall Score</div>
                    </div>
                    <div className="space-y-1.5">
                      {comparePhoto?.criteria?.map((criterion, index) => (
                        <div key={index} className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">{criterion?.name}</span>
                          <span 
                            className="font-semibold"
                            style={{ color: getScoreColor(criterion?.score) }}
                          >
                            {criterion?.score?.toFixed(1)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Score Differences */}
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
                <h4 className="text-sm font-semibold text-foreground mb-3">Score Differences</h4>
                <div className="space-y-2">
                  {selectedPhoto?.criteria?.map((criterion, index) => {
                    const diff = getScoreDifference(
                      criterion?.score,
                      comparePhoto?.criteria?.[index]?.score
                    );
                    return (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-xs text-foreground">{criterion?.name}</span>
                        <div className="flex items-center gap-1.5">
                          <Icon
                            name={diff?.isPositive ? "TrendingUp" : "TrendingDown"}
                            size={14}
                            color={diff?.color}
                          />
                          <span 
                            className="text-xs font-semibold"
                            style={{ color: diff?.color }}
                          >
                            {diff?.isPositive ? '+' : '-'}{diff?.value}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComparisonModal;